import { useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { ControlledSource } from "@/api/filth-source-control-api"

import { Check, ChevronDown, Copy } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Progress } from "@/components/ui/progress"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { FilthAddSourceDialog } from "./filth-add-source-dialog"
import { FilthEditSourceDialog } from "./filth-edit-source-dialog"
import { FilthRemoveSourceDialog } from "./filth-remove-source-dialog"
import {
  controlledSource,
  sourceControlError,
  sourceControlKey,
  sourceTargetDisplay,
} from "./filth-source-control-utils"
import { FilthSourceRowActions } from "./filth-source-row-actions"
import {
  filthFinishedAt,
  filthJobBadgeVariant,
  filthJobPhaseKey,
  filthJobProgress,
  filthSourcesError,
} from "./filth-sources-utils"
import { useFilthSourceControl, useFilthSources } from "../hooks/use-filth-sources"

function CopyTargetButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      aria-label={label}
      className="size-7 shrink-0"
      size="icon"
      type="button"
      variant="ghost"
      onClick={() => {
        void navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  )
}

export function FilthSourcesPanel({ backendId }: { backendId: string }) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [cursors, setCursors] = useState<string[]>([])
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<ControlledSource | null>(null)
  const [removing, setRemoving] = useState<ControlledSource | null>(null)
  const [effect, setEffect] = useState<string | null>(null)
  const client = useQueryClient()
  const { data, isLoading, isFetching, error, refetch } = useFilthSources(
    backendId,
    open,
    cursors.at(-1)
  )
  const control = useFilthSourceControl(backendId, open)
  const descriptor = control.data?.supported === false ? null : control.data
  const canManage = !!descriptor?.source_kinds.length
  const changed = (effect?: string) => {
    setEffect(effect === "resync_required" ? effect : null)
    void client.invalidateQueries({ queryKey: ["admin", "filthBackends", backendId] })
    void client.invalidateQueries({ queryKey: ["filth-libraries", backendId] })
    void client.invalidateQueries({ queryKey: ["filth-remove", backendId] })
  }
  const key = "adminDashboard.filthBackends.sources"

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button className="gap-2" size="sm" variant="ghost">
          <ChevronDown
            aria-hidden="true"
            className={`size-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
          {t(`${key}.title`)}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 pt-4">
        <div className="flex justify-end gap-2">
          {canManage && descriptor.capabilities.includes("connector.admin") && (
            <Button size="sm" onClick={() => setAdding(true)}>
              {t(`${sourceControlKey}.add`)}
            </Button>
          )}
          <Button disabled={isFetching} size="sm" variant="outline" onClick={() => void refetch()}>
            {t(`${key}.refresh`)}
          </Button>
        </div>
        {control.error && (
          <p className="text-sm" role="alert">
            {t(`${sourceControlKey}.errors.${sourceControlError(control.error)}`)}
          </p>
        )}
        {effect && (
          <p className="text-sm" role="status">
            {t(`${sourceControlKey}.resyncRequired`)}
          </p>
        )}
        {isLoading && (
          <p className="text-muted-foreground text-sm" role="status">
            {t(`${key}.loading`)}
          </p>
        )}
        {error && (
          <p className="text-sm" role="alert">
            {t(`${key}.errors.${filthSourcesError(error)}`)}
          </p>
        )}
        {!isLoading && !error && data?.connectors.length === 0 && (
          <p className="text-muted-foreground py-8 text-center text-sm">{t(`${key}.empty`)}</p>
        )}
        {!isLoading && !error && !!data?.connectors.length && (
          <Table className="min-w-[56rem]">
            <TableHeader>
              <TableRow>
                {[
                  "target",
                  "kind",
                  "enabled",
                  "status",
                  "phase",
                  "lastFinished",
                  "progress",
                  "errorCode",
                ].map((column) => (
                  <TableHead key={column}>{t(`${key}.col.${column}`)}</TableHead>
                ))}
                {canManage && <TableHead>{t(`${sourceControlKey}.actions`)}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.connectors.map((source) => {
                const controlled = controlledSource(source)
                const job = source.latest_job
                const progress = filthJobProgress(job)
                const phaseKey = filthJobPhaseKey(job?.phase)
                const target = sourceTargetDisplay(source.target)
                return (
                  <TableRow key={source.connector_uid}>
                    <TableCell className="max-w-64">
                      <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0" title={target.raw}>
                          <div className="truncate font-medium">{target.primary}</div>
                          {target.secondary && (
                            <div className="text-muted-foreground truncate text-xs">
                              {target.secondary}
                            </div>
                          )}
                        </div>
                        <CopyTargetButton label={t(`${key}.copyTarget`)} value={target.raw} />
                      </div>
                    </TableCell>
                    <TableCell>{source.kind}</TableCell>
                    <TableCell>{t(`${key}.${source.enabled ? "enabled" : "disabled"}`)}</TableCell>
                    <TableCell>
                      <Badge className="shadow-none" variant={filthJobBadgeVariant(job?.status)}>
                        {job?.status ?? t(`${key}.noJob`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {phaseKey ? t(`${key}.phases.${phaseKey}`) : (job?.phase ?? "—")}
                    </TableCell>
                    <TableCell>{filthFinishedAt(job?.finished_at, i18n.language)}</TableCell>
                    <TableCell className="min-w-32">
                      {progress == null ? (
                        "—"
                      ) : (
                        <div className="space-y-2">
                          <Progress
                            aria-label={t(`${key}.col.progress`)}
                            aria-valuenow={progress}
                            className="bg-muted border-foreground border"
                            indicatorClassName="border-foreground border-r"
                            value={progress}
                          />
                          <span className="text-muted-foreground text-xs">
                            {job?.entities_done?.toLocaleString(i18n.language)} /{" "}
                            {job?.entities_total?.toLocaleString(i18n.language)}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-48 font-mono text-xs break-words whitespace-normal">
                      {job?.error_code ?? "—"}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        {controlled && (
                          <FilthSourceRowActions
                            backendId={backendId}
                            capabilities={descriptor.capabilities}
                            source={controlled}
                            onChanged={changed}
                            onEdit={() => setEditing(controlled)}
                            onRemove={() => setRemoving(controlled)}
                          />
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
        {(!!cursors.length || data?.next) && (
          <div className="flex justify-end gap-2">
            <Button
              disabled={isFetching || !cursors.length}
              variant="outline"
              onClick={() => setCursors((v) => v.slice(0, -1))}
            >
              {t(`${sourceControlKey}.back`)}
            </Button>
            <Button
              disabled={isFetching || !data?.next}
              variant="outline"
              onClick={() => {
                if (data?.next) setCursors((v) => [...v, data.next!])
              }}
            >
              {t(`${sourceControlKey}.next`)}
            </Button>
          </div>
        )}
        {adding && descriptor && (
          <FilthAddSourceDialog
            backendId={backendId}
            descriptor={descriptor}
            onClose={() => setAdding(false)}
            onSaved={changed}
          />
        )}
        {editing && descriptor && (
          <FilthEditSourceDialog
            backendId={backendId}
            descriptor={descriptor}
            source={editing}
            onClose={() => setEditing(null)}
            onSaved={changed}
          />
        )}
        {removing && (
          <FilthRemoveSourceDialog
            backendId={backendId}
            source={removing}
            onClose={() => setRemoving(null)}
            onSaved={changed}
          />
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
